import { Box, useTheme } from '@mui/material';
import { getFixedT } from 'i18next';
import React, { useCallback } from 'react';

import { EstateImage } from '../../../../EstateImage/EstateImage';
import { getStyles } from '../ImageSection.styles';
import { ImageObject } from '../ImageSection.types';

const t = getFixedT(null, 'core-immobilien');

interface ClickableImageContainerProps {
  imageObject: ImageObject;
  onClick: React.MouseEventHandler;
  additionalContent?: React.ReactNode;
}

export const ClickableImageContainer: React.FC<ClickableImageContainerProps> = ({
  imageObject,
  onClick,
  additionalContent,
}) => {
  const theme = useTheme();
  const styles = getStyles(theme);

  const handleClick = useCallback<React.MouseEventHandler>(
    (event) => {
      if (imageObject) {
        onClick(event);
      }
    },
    [imageObject, onClick]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (!imageObject) {
        return;
      }

      if (event.key === 'Enter' || event.key === ' ') {
        onClick(event as unknown as React.MouseEvent);
        event.preventDefault();
      }
    },
    [imageObject, onClick]
  );

  return (
    <Box
      sx={{
        ...styles.estateImageContainer,
        cursor: imageObject ? 'pointer' : 'default',
      }}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-label={
        imageObject
          ? `${t('detailPage.previewImage')} ${imageObject?.description ?? ''}`
          : t('detailPage.noPreviewImage')
      }
      role="button"
      tabIndex={imageObject ? 0 : -1}
      className={imageObject ? 'image-container' : 'image-container placeholder'}
    >
      {imageObject && <EstateImage imageObject={imageObject} imageStyle={styles.estateImage as React.CSSProperties} />}
      {additionalContent}
    </Box>
  );
};

ClickableImageContainer.displayName = 'ClickableImageContainer';
